import { Injectable, signal, effect } from '@angular/core';

/**
 * Available theme types
 */
type ThemeType = 'light' | 'dark' | 'light-purple';

/**
 * Theme Service
 * Manages application theme switching (light/dark/light-purple mode)
 * Uses Angular signals for reactive theme changes
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  // Available themes
  private readonly THEMES = {
    LIGHT: 'light' as ThemeType,
    DARK: 'dark' as ThemeType,
    LIGHT_PURPLE: 'light-purple' as ThemeType
  } as const;

  // Storage key for theme preference
  private readonly THEME_STORAGE_KEY = 'app-theme';

  // Current theme signal
  readonly currentTheme = signal<ThemeType>(this.THEMES.LIGHT);

  // Computed signal for isDark
  readonly isDarkMode = signal<boolean>(false);

  constructor() {
    // Load saved theme from localStorage on initialization
    this.loadThemeFromStorage();

    // Apply theme whenever it changes
    effect(() => {
      this.applyTheme(this.currentTheme());
    });
  }

  /**
   * Toggle between all available themes (light → light-purple → dark → light)
   */
  toggleTheme(): void {
    const current = this.currentTheme();
    let newTheme: ThemeType;
    
    // Cycle: light → light-purple → dark → light
    if (current === this.THEMES.LIGHT) {
      newTheme = this.THEMES.LIGHT_PURPLE;
    } else if (current === this.THEMES.LIGHT_PURPLE) {
      newTheme = this.THEMES.DARK;
    } else {
      newTheme = this.THEMES.LIGHT;
    }
    
    this.setTheme(newTheme);
  }

  /**
   * Set specific theme
   * @param theme - 'light', 'dark', or 'light-purple'
   */
  setTheme(theme: string): void {
    // Validate theme
    const validThemes: ThemeType[] = [this.THEMES.LIGHT, this.THEMES.DARK, this.THEMES.LIGHT_PURPLE];
    if (!validThemes.includes(theme as ThemeType)) {
      console.warn(`Invalid theme: ${theme}. Using light theme.`);
      theme = this.THEMES.LIGHT;
    }

    this.currentTheme.set(theme as ThemeType);
    this.isDarkMode.set(theme === this.THEMES.DARK);
    this.saveThemeToStorage(theme);
  }

  /**
   * Get current theme value
   */
  getTheme(): ThemeType {
    return this.currentTheme();
  }

  /**
   * Check if dark mode is active
   */
  isDark(): boolean {
    return this.isDarkMode();
  }

  /**
   * Apply theme to document
   */
  private applyTheme(theme: string): void {
    const htmlElement = document.documentElement;
    
    // Remove all theme classes and attributes
    htmlElement.classList.remove('app-dark');
    htmlElement.removeAttribute('data-theme');
    
    // Apply new theme
    if (theme === this.THEMES.DARK) {
      htmlElement.classList.add('app-dark'); // For PrimeNG darkModeSelector
      htmlElement.setAttribute('data-theme', 'dark');
    } else if (theme === this.THEMES.LIGHT_PURPLE) {
      htmlElement.setAttribute('data-theme', 'light-purple');
      // No app-dark class - this is a light theme
    }
    // Light theme is default, no class/attribute needed
    
    console.log(`Theme applied: ${theme}`);
  }

  /**
   * Load theme from localStorage
   */
  private loadThemeFromStorage(): void {
    try {
      const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
      if (savedTheme) {
        this.setTheme(savedTheme);
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.setTheme(prefersDark ? this.THEMES.DARK : this.THEMES.LIGHT);
      }
    } catch (error) {
      console.error('Error loading theme from storage:', error);
      this.setTheme(this.THEMES.LIGHT);
    }
  }

  /**
   * Save theme to localStorage
   */
  private saveThemeToStorage(theme: string): void {
    try {
      localStorage.setItem(this.THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.error('Error saving theme to storage:', error);
    }
  }

  /**
   * Listen to system theme changes
   */
  listenToSystemTheme(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    mediaQuery.addEventListener('change', (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
      if (!savedTheme) {
        this.setTheme(e.matches ? this.THEMES.DARK : this.THEMES.LIGHT);
      }
    });
  }
}
